import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { DocumentModel } from '@data-layer/models/documents';
import { IBillOfLadingFormData } from '@app/modules/entry-summary/interfaces';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { enumChargesDistributionType, enumTransportationMode } from '@app/data-layer/enums/entry-summaries';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { FormHelper } from '@helpers/form.helper';
import { ShipmentModel, UnitModel } from '@data-layer/models/common';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryStateService, EsCreateBlFormService, EsLabelsService, EsValidationsHelperService } from '@shared/es-mutual/services';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeRepository } from '@data-layer/repositories';
import { esBlFormData } from '@shared/es-mutual/const';

@Component({
  selector: 'es-bill-of-lading-form-desktop',
  template: `
    <form class="bl-form" autocomplete="off" [formGroup]="formGroup" (ngSubmit)="saveData()">
      <div class="es-bl-body-details-body-form">
        <p class="caption-hint" *ngIf="!isRail" [innerHTML]="'ENTRY_SUMMARY.SCAC_HINT' | translate"></p>
        <p class="caption-hint" *ngIf="isRail" [innerHTML]="'INFO.BL.SUB_TITLE_RAIL' | translate"></p>

        <div class="form-row">
          <div class="col-6">
            <app-control-wrapper
              [control]="formGroup.get(['masterBill', 'carrier'])"
              [errors]="errors.masterBillCarrier"
              [helper]="billOfLadingData.carrier.helper"
            >
              <label for="sidebar-masterBillNumber" control-label>
                {{ billOfLadingData.carrier.titleMaster | translate }}

                <app-hint-tooltip-icon [hint]="billOfLadingData.carrier.tooltip | translate"></app-hint-tooltip-icon>
              </label>
              <input
                type="text"
                [formControl]="formGroup.get(['masterBill', 'carrier'])"
                class="form-control"
                id="sidebar-masterBillCarrier"
                name="masterBillCarrier"
                [mask]="billOfLadingData.carrier.maskMaster"
                [attr.inputmode]="billOfLadingData.carrier.inputmodeMaster"
                [attr.pattern]="billOfLadingData.carrier.patternMaster"
                [appMaskErrorTooltip]="billOfLadingData.carrier.maskMessageMaster"
                [maxMaskLength]="billOfLadingData.carrier.maxMaskLength"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
          <div class="col-6">
            <app-control-wrapper
              [control]="formGroup.get(['masterBill', 'number'])"
              [errors]="errors.masterBillNumber"
              [helper]="billOfLadingData.number.helper"
            >
              <label for="sidebar-masterBillNumber" control-label>
                {{ billOfLadingData.number.titleMaster | translate }}

                <app-hint-tooltip-icon [hint]="billOfLadingData.number.tooltip | translate"></app-hint-tooltip-icon>
              </label>
              <input
                type="text"
                [formControl]="formGroup.get(['masterBill', 'number'])"
                class="form-control"
                id="sidebar-masterBillNumber"
                name="masterBillNumber"
                [mask]="billOfLadingData.number.maskMaster"
                [attr.inputmode]="billOfLadingData.number.inputmodeMaster"
                [attr.pattern]="billOfLadingData.number.patternMaster"
                [appMaskErrorTooltip]="billOfLadingData.number.maskMessageMaster"
                [maxMaskLength]="billOfLadingData.number.maxMaskLength"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
        </div>

        <div class="form-row" *ngIf="!isRail && !isTruck">
          <div class="col-6">
            <app-control-wrapper
              [control]="formGroup.get(['houseBill', 'carrier'])"
              [errors]="errors.houseBillCarrier"
              [helper]="billOfLadingData.carrier.helper"
            >
              <label for="sidebar-houseBillCarrier" control-label>
                {{ billOfLadingData.carrier.titleHouse | translate }}

                <app-hint-tooltip-icon [hint]="billOfLadingData.carrier.tooltip | translate"></app-hint-tooltip-icon>
              </label>
              <input
                type="text"
                [formControl]="formGroup.get(['houseBill', 'carrier'])"
                class="form-control"
                id="sidebar-houseBillCarrier"
                name="houseBillCarrier"
                [mask]="billOfLadingData.carrier.maskHouse"
                [validation]="false"
                [attr.inputmode]="billOfLadingData.carrier.inputmodeHouse"
                [attr.pattern]="billOfLadingData.carrier.patternHouse"
                [appMaskErrorTooltip]="billOfLadingData.carrier.maskMessageHouse"
                [maxMaskLength]="billOfLadingData.carrier.maxMaskLength"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
          <div class="col-6">
            <app-control-wrapper
              [control]="formGroup.get(['houseBill', 'number'])"
              [errors]="errors.houseBillNumber"
              [helper]="billOfLadingData.number.helper"
            >
              <label for="sidebar-houseBillNumber" control-label>
                {{ billOfLadingData.number.titleHouse | translate }}

                <app-hint-tooltip-icon [hint]="billOfLadingData.number.tooltip | translate"></app-hint-tooltip-icon>
              </label>
              <input
                type="text"
                [formControl]="formGroup.get(['houseBill', 'number'])"
                class="form-control"
                id="sidebar-houseBillNumber"
                name="houseBillNumber"
                [mask]="billOfLadingData.number.maskHouse"
                [attr.inputmode]="billOfLadingData.number.inputmodeHouse"
                [attr.pattern]="billOfLadingData.number.patternHouse"
                [appMaskErrorTooltip]="billOfLadingData.number.maskMessageHouse"
                [maxMaskLength]="billOfLadingData.number.maxMaskLength"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
        </div>

        <div class="form-row">
          <div class="col-12">
            <p class="caption-hint">{{ 'ENTRY_SUMMARY.FILL_TOTAL_QUANTITY' | translate }}</p>
          </div>
          <div class="col-3">
            <app-control-wrapper [control]="formGroup.get('quantity')" [errors]="errors">
              <label for="sidebar-quantity" control-label>{{ 'FORM_LABEL.TOTAL_QUANTITY' | translate }}</label>
              <input
                type="text"
                [formControl]="formGroup.get('quantity')"
                class="form-control"
                id="sidebar-quantity"
                name="quantity"
                [pattern]="decimalInputData.pattern"
                [mask]="decimalInputData.mask"
                [dropSpecialCharacters]="false"
                [appMaskErrorTooltip]="decimalInputData.maskErrorTooltip"
                inputmode="number"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
          <div class="col-6">
            <app-control-wrapper [control]="formGroup.get('quantityUnit')" [errors]="errors">
              <label for="sidebar-unitOfMeasure" control-label>{{ 'FORM_LABEL.UNIT_OF_MEASURE' | translate }}</label>
              <ng-select
                id="sidebar-unitOfMeasure"
                class="custom"
                [formControl]="formGroup.get('quantityUnit')"
                [items]="quantityUnits"
                [bindLabel]="'code'"
                [bindValue]="'code'"
                [searchFn]="searchQuantityUnit"
                control-wrapper
              >
                <ng-template ng-label-tmp let-item="item">{{ item.code }} {{ item.description }}</ng-template>
                <ng-template ng-option-tmp let-item="item">{{ item.code }} {{ item.description }}</ng-template>
              </ng-select>
            </app-control-wrapper>
          </div>
        </div>

        <div class="form-row" *ngIf="isImmediateTransfer">
          <div class="col-12">
            <p class="caption-hint">
              <span [innerHTML]="'ENTRY_SUMMARY.FILL_IMMEDIATE_TRANSFER' | translate"></span>

              <app-hint-tooltip-icon [hint]="'ENTRY_SUMMARY.FILL_IMMEDIATE_TRANSFER' | translate"></app-hint-tooltip-icon>
            </p>
          </div>
          <div class="col-6">
            <app-control-wrapper [control]="formGroup.get('itNumber')" [errors]="errors.itNumber" [helper]="'FORM_HELPER.IT_NUMBER'">
              <label for="sidebar-itNumber" control-label>{{ 'FORM_LABEL.IT_NUMBER' | translate }}</label>
              <input
                type="text"
                [formControl]="formGroup.get('itNumber')"
                class="form-control"
                id="sidebar-itNumber"
                name="itNumber"
                [mask]="'A{11}'"
                [appMaskErrorTooltip]="'MASK_ERROR.IT_NUMBER'"
                [maxMaskLength]="11"
                inputmode="text"
                [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
          <div class="col-6">
            <date-picker
              [fc]="formGroup.get('itDate')"
              [errors]="errors.itDate"
              [helper]="'FORM_HELPER.IT_DATE'"
              [label]="'FORM_LABEL.IT_DATE'"
              [id]="'sidebar-itDate'"
              [name]="'itDate'"
              [placeholder]="'FORM_PLACEHOLDER.CHOOSE'"
              [minDate]="itMinDate"
              [maxDate]="itMaxDate"
            ></date-picker>
          </div>
        </div>
      </div>

      <div class="sidebar-form-buttons">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="saveData(false)"></missing-fields>
  `,
  styleUrls: ['./es-bill-of-lading-form-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsBillOfLadingFormDesktopComponent implements OnInit, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public shipment: ShipmentModel;
  @Output() public dataSaved: EventEmitter<ShipmentModel> = new EventEmitter<ShipmentModel>();

  public formGroup: UntypedFormGroup;

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public decimalInputData: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public enumEntityModelCode = enumEntityModelCode;
  public enumChargesDistributionType = enumChargesDistributionType;
  public enumIdentifierQualifier = enumIdentifierQualifier;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;

  public currentLabelsData: { titleMaster: string; titleHouse: string; titleCarrier: string; titleNumber: string };

  public attachedDocuments: DocumentModel[] = [];

  public isCanEdit: boolean = false;
  public isCanRemoveDocuments: boolean = false;
  public isShowDocuments: boolean = false;
  public isAir: boolean = false;
  public isRail: boolean = false;
  public isTruck: boolean;

  public isShowIdentifier: boolean;

  public exportMaxDate: string;
  public isImmediateTransfer: boolean = false;
  public hasSingleExportData: boolean = false;
  public quantityUnits = [];

  public errors: any;
  public billOfLadingData: IBillOfLadingFormData;
  public initialForeignPortsItems: any[] = [];

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private fb: UntypedFormBuilder,
    private fh: FormHelper,
    private entrySummaryStateService: EntrySummaryStateService,
    private codeRepository: CodeRepository,
    private esLabelsService: EsLabelsService,
    private esCreateBlFormService: EsCreateBlFormService,
    private esValidationsHelperService: EsValidationsHelperService,
  ) {}

  public ngOnInit(): void {
    this.isAir = this.entrySummary?.transportationMode === enumTransportationMode.AIR;
    this.isRail = this.entrySummary?.transportationMode === enumTransportationMode.RAIL;
    this.isTruck = this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;

    this.formGroup = this.esCreateBlFormService.createForm(false);
    this.esCreateBlFormService.setData(this.entrySummary, this.shipment, this.isCanEdit);
    this.esCreateBlFormService.updateShipmentForm(this.entrySummary, false);

    this.onUpdateEntrySummary();
    if (this.entrySummary?.transportationMode) {
      this.billOfLadingData = esBlFormData[this.entrySummary?.transportationMode] || esBlFormData.default;
    } else {
      this.billOfLadingData = esBlFormData[enumTransportationMode.OCEAN];
    }

    this.updateLabelsData();
    this.getShipmentUnits();

    this.createErrors();

    this.updateTruckValidation();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public getCurrentForeignPort() {
    this.codeRepository
      .getForeignPort(this.formGroup.get('foreignPortOfLadingId').value)
      .pipe(takeUntil(this.destroy$))
      .subscribe((port: any) => {
        this.initialForeignPortsItems = [port];
      });
  }

  public searchQuantityUnit(term: string, item: UnitModel) {
    term = term.toLowerCase().trim();
    return item.code.toLowerCase().indexOf(term) > -1 || item.description.toLowerCase().indexOf(term) > -1;
  }

  public get itMinDate(): string {
    return this.entrySummaryStateService.itMinDate(
      this.entrySummary,
      this.hasSingleExportData ? this.formGroup.get('exportDate').value : null,
    );
  }

  public get itMaxDate(): string {
    return this.entrySummaryStateService.itMaxDate(this.entrySummary);
  }

  public get isWithVia(): boolean {
    return this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode;
  }

  public saveData(needValidate: boolean = true) {
    if (needValidate && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      this.openModal(invalidFields);
      return;
    }

    this.dataSaved.emit(this.fh.emptyStringsToNull(this.formGroup.value));
  }

  private onUpdateEntrySummary() {
    this.exportMaxDate = this.entrySummary?.arrivalDate;

    const { portOfEntryCode, portOfUnladingCode } = this.entrySummary;
    this.hasSingleExportData = this.entrySummary?.hasSingleExportData;
    this.isImmediateTransfer = !this.isTruck
      ? portOfEntryCode !== portOfUnladingCode || !portOfEntryCode || !portOfUnladingCode
      : this.isWithVia;
    if (this.hasSingleExportData && !this.isAir && this.formGroup.get('foreignPortOfLadingId').value) {
      this.getCurrentForeignPort();
    }
  }

  private updateLabelsData() {
    this.currentLabelsData = this.esLabelsService.getLabelsData(this.entrySummary?.transportationMode);
  }

  private getShipmentUnits() {
    this.codeRepository
      .getShipmentUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe((quantityUnits: UnitModel[]) => {
        this.quantityUnits = quantityUnits;
      });
  }

  private openModal(data: any): void {
    this.missingFieldModal.openCustomModal(
      this.esValidationsHelperService.getEsSummaryModalModifiedData(this.entrySummary.transportationMode, data),
    );
  }

  private updateTruckValidation(): void {
    if (this.isTruck) {
      this.formGroup.get('chargesDistributionType').clearValidators();

      if (!this.isWithVia) {
        this.formGroup.get('itDate').clearValidators();
        this.formGroup.get('itNumber').clearValidators();
      }
    }
  }

  private createErrors() {
    this.errors = {
      code: {
        required: 'FORM_ERROR.REQUIRED',
      },
      masterBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      masterBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      required: 'FORM_ERROR.REQUIRED',
    };
  }
}
